import { useMemo, useState } from 'react'
import { Stethoscope, Calendar, ChevronRight, Search, UserRound } from 'lucide-react'
import type { ConsultationSummary } from '@/services/consultation.service'
import { Input } from '@/components/ui/input'
import { ConsultationNoteDialog } from './ConsultationNoteDialog'
import { VITALS, toNumericVital } from '@/lib/vitals'

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'En curso',
  completed: 'Completada',
  locked: 'Bloqueada',
  unlock_requested: 'Desbloqueo solicitado',
}

interface ConsultationNoteListProps {
  items: ConsultationSummary[]
  /** 'full' = roomy cards (Evolution); 'compact' = dense rows (context panel). */
  variant?: 'full' | 'compact'
  /** Show a search box to locate a note by date or diagnosis (REQ-VIS-02d). */
  searchable?: boolean
  emptyText?: string
  /**
   * When provided, a row click delegates to the caller (so a shared note
   * viewer can also be opened from the trend chart). When omitted, the list
   * opens its own read-only dialog.
   */
  onSelect?: (c: ConsultationSummary) => void
}

interface VitalDelta {
  key: string
  label: string
  unit: string
  value: number
  delta: number | null
  higherIsWorse?: boolean
}

/**
 * Per-note vital deltas (REQ-FE-02): for each note, each captured vital is
 * compared against the most recent OLDER note that recorded the same vital.
 * Computed on read from the full set; nothing is stored. Gaps (missing values)
 * are skipped without breaking the comparison chain.
 */
function buildDeltas(items: ConsultationSummary[]): Record<string, VitalDelta[]> {
  const chrono = [...items].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )
  const last: Record<string, number> = {}
  const byId: Record<string, VitalDelta[]> = {}
  for (const c of chrono) {
    const row: VitalDelta[] = []
    for (const v of VITALS) {
      const value = toNumericVital(c.vitals?.[v.key])
      if (value == null) continue
      const prev = last[v.key]
      row.push({
        key: v.key,
        label: v.label,
        unit: v.unit,
        value,
        delta: prev != null ? value - prev : null,
        higherIsWorse: v.higherIsWorse,
      })
      last[v.key] = value
    }
    byId[c.id] = row
  }
  return byId
}

/**
 * Reusable READ-ONLY list of progress notes (consultations).
 *
 * NOM-004 REQ-VIS-03 / REQ-FE-09: notes are never edited from a view. It reads
 * the items it is given (single source — see usePatientConsultations), never
 * storing its own copies (REQ-VIS-01 / REQ-FE-05).
 */
export function ConsultationNoteList({
  items,
  variant = 'full',
  searchable = false,
  emptyText = 'Sin notas registradas.',
  onSelect,
}: ConsultationNoteListProps) {
  const [query, setQuery] = useState('')
  const [internalId, setInternalId] = useState<string | null>(null)

  const deltas = useMemo(() => buildDeltas(items), [items])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((c) => {
      const date = new Date(c.created_at)
        .toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
        .toLowerCase()
      const haystack = [
        date,
        c.attested_by_name ?? '',
        c.chief_complaint ?? '',
        c.assessment ?? '',
        ...(c.diagnosis_codes ?? []),
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [items, query])

  const compact = variant === 'compact'
  const handleClick = (c: ConsultationSummary) => (onSelect ? onSelect(c) : setInternalId(c.id))

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      {searchable && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ml-on-surface-variant/50" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar nota por fecha, médico o diagnóstico (CIE-10)…"
            className="pl-9"
          />
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-ml-on-surface-variant/60 italic py-2">
          {query ? 'Ninguna nota coincide con la búsqueda.' : emptyText}
        </p>
      ) : (
        filtered.map((c) => {
          const d = new Date(c.created_at)
          const vitalDeltas = !compact ? deltas[c.id] ?? [] : []
          return (
            <button
              key={c.id}
              onClick={() => handleClick(c)}
              className={
                'w-full text-left bg-ml-surface-container-lowest rounded-xl border border-ml-outline-variant/10 ' +
                'hover:border-ml-outline-variant/25 transition-all group ' +
                (compact ? 'p-3' : 'p-5')
              }
            >
              <div className="flex items-start gap-3">
                <div
                  className={
                    'rounded-lg bg-ml-primary/10 flex items-center justify-center flex-shrink-0 ' +
                    (compact ? 'w-8 h-8' : 'w-11 h-11')
                  }
                >
                  <Stethoscope className={compact ? 'h-4 w-4 text-ml-primary' : 'h-5 w-5 text-ml-primary'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 text-xs text-ml-on-surface-variant">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {' · '}
                      {d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {c.attested_by_name && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-ml-outline-variant" />
                        <span className="flex items-center gap-1">
                          <UserRound className="h-3 w-3" />
                          {c.attested_by_name}
                          {c.attested_by_cedula ? ` · Céd. ${c.attested_by_cedula}` : ''}
                        </span>
                      </>
                    )}
                    <span className="w-1 h-1 rounded-full bg-ml-outline-variant" />
                    <span>{STATUS_LABELS[c.status] ?? c.status}</span>
                  </div>
                  {c.chief_complaint && (
                    <p className={'text-ml-on-surface mt-1 line-clamp-2 ' + (compact ? 'text-xs' : 'text-sm')}>
                      {c.chief_complaint}
                    </p>
                  )}
                  {(c.diagnosis_codes?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {c.diagnosis_codes.map((dx, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-widest bg-ml-primary-fixed text-ml-primary"
                        >
                          {dx}
                        </span>
                      ))}
                    </div>
                  )}
                  {vitalDeltas.length > 0 && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-ml-on-surface-variant">
                      {vitalDeltas.map((v) => {
                        const dir = v.delta == null || v.delta === 0 ? 0 : v.delta > 0 ? 1 : -1
                        const adverse = v.higherIsWorse == null ? null : dir === (v.higherIsWorse ? 1 : -1)
                        const color =
                          dir === 0 || adverse == null
                            ? 'text-ml-on-surface-variant/60'
                            : adverse
                              ? 'text-red-600'
                              : 'text-teal-600'
                        return (
                          <span key={v.key} className="whitespace-nowrap">
                            {v.label} <span className="text-ml-on-surface">{v.value}</span>
                            <span className="text-ml-on-surface-variant/50">{v.unit}</span>
                            {v.delta != null && v.delta !== 0 && (
                              <span className={color}>
                                {' '}({v.delta > 0 ? '+' : ''}
                                {v.delta.toFixed(1)})
                              </span>
                            )}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-ml-on-surface-variant/40 group-hover:text-ml-primary transition-all flex-shrink-0" />
              </div>
            </button>
          )
        })
      )}

      {/* Internal read-only viewer — used only when the caller didn't take over
          selection with `onSelect` (REQ-FE-03 / REQ-FE-09). */}
      {!onSelect && (
        <ConsultationNoteDialog consultationId={internalId} onClose={() => setInternalId(null)} />
      )}
    </div>
  )
}
